<template>
    <article>
        <p>
            This inspection detects situations in which an application is experiencing a lack of CPU time,
            which can be caused by several reasons:
        </p>
        <ul>
            <li>A container has reached its CPU limit and has been throttled by the system.</li>
            <li>A container competes for CPU time against the other containers running on the same node.</li>
            <li>A container consumes all available CPU time on its own.</li>
        </ul>

        <v-card>
            <v-img eager src="/static/img/docs/coroot-ce/inspections/cpu.png" />
        </v-card>


        <h2>Possible failure scenarios</h2>

        <h3>A container has reached its CPU limit</h3>
        <p>
            To control the CPU usage of containers, you can set CPU limits for them.
            If a container uses all the allowed CPU bandwidth, it will be limited in CPU cycles for a while.
            This mechanism is called <i>CPU throttling</i>.
        </p>

        <h3>High CPU utilization on the related nodes</h3>
        <p>
            Throttling is not the sole reason why an application can experience a shortage of CPU time.
            There can be situations where the application itself or other applications on the same node consume the entire CPU bandwidth.
            This can result in performance degradation due to a shortage of CPU time.
        </p>


        <h2>Dashboard</h2>

        <h3 id="usage">CPU usage</h3>
        <v-card max-width="550">
            <v-img eager src="/static/img/docs/coroot-ce/inspections/cpu-usage.png" />
        </v-card>
        <p>This chart can help you answer the following questions:</p>
        <ul>
            <li>Are all the application instances consuming the same amount of CPU time, or are there any outliers?</li>
            <li>How does CPU consumption change over time?</li>
            <li>Assess the CPU usage of each container in comparison to its limit.</li>
        </ul>
        <p>
            CPU usage is calculated using the <MetricLink name="container_resources_cpu_usage_seconds_total" exporter="node-agent" /> metric.
        </p>
        <p>
            If the application Pods contain more then one container,
            this chart provides you with both per-container and total views.
        </p>
        <p>
            The <v-btn x-small color="primary" class="caption">profile</v-btn> button opens the CPU profiling data,
            allowing you to identify and analyze unexpected spikes in CPU usage down to the precise line of code.
        </p>
        <v-alert icon="mdi-lightbulb-on-outline" color="grey" outlined>
            Learn more about <router-link :to="{name: 'docs', params: {chapter: 'coroot', group: 'profiling'}}">Continuous profiling</router-link> in Coroot.
        </v-alert>

        <h3 id="delay">CPU delay</h3>
        <v-card max-width="550">
            <v-img eager src="/static/img/docs/coroot-ce/inspections/cpu-delay.png" />
        </v-card>
        <p>
            A lack of CPU time can be estimated by <MetricLink name="container_resources_cpu_delay_seconds_total" exporter="node-agent"/> metric.
            The Linux kernel reports <a href="https://www.kernel.org/doc/html/latest/accounting/delay-accounting.html" target="_blank" rel="noopener noreferrer">CPU delay</a>,
            indicating how long a specific process or container has been waiting for CPU time.
        </p>
        <p>
            For instance, if you observe a delay of 500ms per second,
            it signifies that you are experiencing an additional latency of 500ms,
            which is spread across all requests processed during that wall-clock second.
        </p>
        <p>
            If a container is limited in CPU time (throttled), the <MetricLink name="container_resources_cpu_delay_seconds_total" exporter="node-agent"/> metric is bound to increase.
            This means that this metric indicates a shortage regardless of its underlying reasons.
            To identify the specific reason, please refer to the <i>Throttled time</i> and <i>Node CPU usage</i> usage charts.
        </p>
        <v-alert icon="mdi-post" color="grey" outlined>
            Related blog post:
            <a href="/blog/linux-delay-accounting">Delay accounting: an underrated feature of the Linux kernel</a>
        </v-alert>

        <h3 id="throttled_time">Throttled time</h3>
        <v-card max-width="550">
            <v-img eager src="/static/img/docs/coroot-ce/inspections/cpu-throttled-time.png" />
        </v-card>
        <p>
            The Linux kernel offers several mechanisms to manage the distribution of CPU time among applications.
            One of these mechanisms is CPU Bandwidth Control, which allows to set a quota (limit) for the amount of CPU time that a container (cgroup) is allowed to consume.
            This quota is configured within a specific time period.
            If a container consumes all the allowed CPU bandwidth, it will be limited (throttled) in CPU cycles for the remainder of that period.
        </p>
        <p>
            The <MetricLink name="container_resources_cpu_throttled_seconds_total" exporter="node-agent"/> metric displays the duration for which each container has been throttled.
            Think of this throttled time as an additional latency for the application.
            In simpler terms, if an application has been throttled for 200ms per second,
            it means that the application was suspended for 200ms during that particular wall-clock second.
        </p>
        <p>
            The default quota period in Kubernetes is 100ms.
            Therefore, when you define a CPU limit of 100m (which is equivalent to 1/10 of a CPU core) in Kubernetes,
            it sets up a CPU quota of 10ms for each 100ms period.
            This clarifies situations where the container has been throttled, even though its CPU consumption was significantly lower than the limit:
        </p>
        <v-row no-gutters>
            <v-col>
                <v-card max-width="550">
                    <v-img eager src="/static/img/docs/coroot-ce/inspections/cpu-usage.png" />
                </v-card>
            </v-col>
            <v-col>
                <v-card max-width="550">
                    <v-img eager src="/static/img/docs/coroot-ce/inspections/cpu-throttled-time.png" />
                </v-card>
            </v-col>
        </v-row>

        <h3 id="node_usage">Node CPU usage</h3>
        <v-row no-gutters>
            <v-col>
                <v-card max-width="550">
                    <v-img eager src="/static/img/docs/coroot-ce/inspections/cpu-nodes.png" />
                </v-card>
            </v-col>
            <v-col>
                <v-card max-width="550">
                    <v-img eager src="/static/img/docs/coroot-ce/inspections/cpu-node.png" />
                </v-card>
            </v-col>
        </v-row>
        <p>
            This chart allows you to estimate the CPU usage of the nodes where application instances are running.
            You can compare node CPU usage in an overview mode or choose a specific node to see its CPU usage broken down by <i>mode</i>.
            Each of these modes provides insight into how the CPU's time is allocated across various types of processes and activities:
        </p>
        <ul>
            <li>
                <b>User</b>:
                This mode represents the CPU time consumed by normal user processes running in user space.
                These are typical applications and tasks initiated by users.
            </li>
            <li>
                <b>Nice</b>:
                The "nice" mode is for processes that are niced, meaning they have their execution priority adjusted.
                Niced processes also run in user mode but with lower priority.
            </li>
            <li>
                <b>System</b>:
                This mode accounts for CPU time spent on tasks running in kernel mode, primarily associated with system processes and managing the operating system itself.
            </li>
            <li>
                <b>I/O Wait</b>:
                This mode is associated with the CPU waiting for input/output (I/O) operations to complete.
                During this time, the CPU is idle because it's waiting for data to be read from or written to a storage device.
            </li>
            <li>
                <b>IRQ</b>: The "IRQ" mode indicates CPU time spent servicing hardware interrupts.
                These are signals generated by hardware devices that require immediate attention from the CPU.
            </li>
            <li>
                <b>SoftIRQ</b>:
                Similar to IRQ, "SoftIRQ" mode accounts for CPU time spent servicing software-generated interrupts, known as softirqs.
                These are typically used for network-related tasks and other asynchronous events.
            </li>
            <li>
                <b>Steal</b>:
                This mode indicates the CPU time that a virtual CPU (vCPU) within a virtual machine has to wait
                because the physical CPU on the host system is being utilized by other VMs or processes.
                It essentially reflects the amount of time the vCPU is "stolen" or preempted by other workloads on the host.
                This can have a notable impact on application performance,
                especially when using <a href="https://docs.aws.amazon.com/AWSEC2/latest/UserGuide/burstable-performance-instances.html" target="_blank">burstable performance instances</a>
                like AWS's T4g, T3a, and T3, where resource allocation can vary based on demand.
            </li>
        </ul>
        <p>
            The chart is based on the <MetricLink name="node_resources_cpu_usage_seconds_total" exporter="node-agent"/> metric.
        </p>

        <h3 id="consumers">CPU consumers</h3>
        <v-row no-gutters>
            <v-col>
                <v-card max-width="550">
                    <v-img eager src="/static/img/docs/coroot-ce/inspections/cpu-consumers-1.png" />
                </v-card>
            </v-col>
            <v-col>
                <v-card max-width="550">
                    <v-img eager src="/static/img/docs/coroot-ce/inspections/cpu-consumers-2.png" />
                </v-card>
            </v-col>
        </v-row>
        <p>
            When you observe high CPU usage on a particular node,
            this chart can assist you in identifying the primary CPU-consuming applications.
            The chart displays the top 5 applications by their peak CPU time consumption.
        </p>
        <p>
            The chart is based on the <MetricLink name="container_resources_cpu_usage_seconds_total" exporter="node-agent" /> metric.
        </p>

    </article>
</template>

<script>
import MetricLink from "@/components/MetricLink.vue";

export default {
    components: {MetricLink}
}
</script>

<style scoped>
b {
    font-weight: 600;
}
</style>
